import React, { useEffect, useState } from "react";
import { IAnalysedProperty, IPropertyDepreciationEstimation } from "../../../../../interface/addAnalyser";
import { useAnalysedPropertyContext } from "../AnalysedPropertyContext";
import { REICheckBox, REIDropDown, REIInputNumber } from "../../../../../components/ui";
import {
  AU_CAPITAL_CITY,
  AU_CONSTRUCTION_YEAR,
  NZ_RENOVATIONS,
  PROPERTY_QUALITY,
  PROPERTY_TYPE,
} from "../../../../../utils/constants";
import { WBLogo } from "../../../../../images";
import { VaultLogo as ValutTLogo } from "../../../../../images";
import InputSlider from "../../../../../components/ui/slider";

interface IDepreciationEstimation {
  setIsValid: (isValid: boolean) => void;
  updateProperty: (updated: Partial<IAnalysedProperty>) => void;
}

const DepreciationEstimation: React.FC<IDepreciationEstimation> = ({ setIsValid, updateProperty }) => {
  const { analysedProperty } = useAnalysedPropertyContext();
  const [depreciationEstimation, setDepreciationEstimation] = useState<IPropertyDepreciationEstimation>();
  const [reset, setReset] = useState<boolean>(false);
  const [errors, setErrors] = useState<{
    WBPropertyTypeId?: string;
    NZEstimatedLandValue?: string;
    ConstructionYearComplete?: string;
  }>({});

  useEffect(() => {
    if (analysedProperty && !reset) {
      setDepreciationEstimation(analysedProperty.TaxMarket.PropertyDepreciationEstimation);
      setReset(true);
    }
  }, [analysedProperty]);

  useEffect(() => {
    if (depreciationEstimation) {
      if (analysedProperty?.PropertyDetail.CarportId == 1 && depreciationEstimation.NearestCapitalCity == 0) {
        setIsValid(false);
      } else {
        setIsValid(true);
      }
      updateProperty({
        ...analysedProperty,
        TaxMarket: {
          ...analysedProperty?.TaxMarket,
          PropertyDepreciationEstimation: depreciationEstimation,
          PropertyMarketFactor: analysedProperty?.TaxMarket.PropertyMarketFactor,
          PropertyTaxRate: analysedProperty?.TaxMarket.PropertyTaxRate,
        },
      });
    }
  }, [depreciationEstimation]);

  useEffect(() => {
    if (depreciationEstimation) {
      const isValid = validate();
      setIsValid(
        isValid && !(analysedProperty?.PropertyDetail.CarportId == 1 && depreciationEstimation.NearestCapitalCity == 0)
      );
      updateProperty({
        ...analysedProperty,
        TaxMarket: {
          ...analysedProperty?.TaxMarket,
          PropertyDepreciationEstimation: depreciationEstimation,
          PropertyMarketFactor: analysedProperty?.TaxMarket.PropertyMarketFactor,
          PropertyTaxRate: analysedProperty?.TaxMarket.PropertyTaxRate,
        },
      });
    }
  }, [depreciationEstimation]);

  const setValues = (name: string, value: string | number) => {
    setDepreciationEstimation((prevState) => {
      if (!prevState) return undefined;

      return {
        ...prevState,
        [name]: value !== undefined ? value : 0,
      };
    });
  };

  const validate = () => {
    let isValid = true;
    const newErrors: { WBPropertyTypeId?: string; NZEstimatedLandValue?: string; ConstructionYearComplete?: string } =
      {};

    if (
      analysedProperty?.PropertyDetail.CountryId === 1 &&
      (!depreciationEstimation?.WBPropertyTypeId || depreciationEstimation.WBPropertyTypeId.toString() === "0")
    ) {
      newErrors.WBPropertyTypeId = "Property type is required.";
      isValid = false;
    }

    if (
      analysedProperty?.PropertyDetail.CountryId === 2 &&
      (!depreciationEstimation?.NZEstimatedLandValue || depreciationEstimation.NZEstimatedLandValue.toString() === "0")
    ) {
      newErrors.NZEstimatedLandValue = "Estimated land value is required.";
      isValid = false;
    }

    if (
      analysedProperty?.PropertyDetail.CountryId === 2 &&
      (!depreciationEstimation?.ConstructionYearComplete ||
        depreciationEstimation.ConstructionYearComplete.toString() === "")
    ) {
      newErrors.ConstructionYearComplete = "Construction year is required.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  return (
    <div>
      <div className="relative rounded-lg pl-3 pr-1 py-3 border-solid border border-gray-300 bg-white mt-2">
        <div className="default-profile-header-container">
          <h3 className="text-[var(--primary-color)] mt-0 m-1 text-lg">Depreciation estimation</h3>
        </div>
        <div className="p-4">
          <div className="flex flex-wrap mb-4">
            {analysedProperty?.PropertyDetail.CountryId == 1 && (
              <div className="w-full text-left md:w-1/2 p-2">
                <div className="flex items-center">
                  <a
                    id="depe-lnk-wbreport"
                    data-testid="depe-lnk-wbreport"
                    target="_blank"
                    href="http://www.realestateinvestar.com.au/depreciationreport/"
                  >
                    <em className="float-left">Powered by</em>
                    <img className="float-left ml-4 mr-4 w-20" src={WBLogo}></img>
                    <div className="float-left">
                      <strong>Washington Brown</strong>
                      <br />
                      Order depreciation report
                    </div>
                  </a>
                </div>
              </div>
            )}
            {analysedProperty?.PropertyDetail.CountryId == 2 && (
              <div className="w-full  md:w-1/2 p-2">
                <div className="flex">
                  <a
                    id="depe-lnk-valutreport"
                    data-testid="depe-lnk-valutreport"
                    target="_blank"
                    href="http://www.realestateinvestar.co.nz/depreciation/"
                  >
                    <em className="float-left">Powered by</em>
                    <img className="float-left ml-4 mr-4 w-20" src={ValutTLogo}></img>
                    <div className="float-left">
                      <strong>ValulT</strong>
                      <br />
                      Specialists in property depreciation
                    </div>
                  </a>
                </div>
              </div>
            )}
            <div className="w-full md:w-1/2 p-2">
              <div className="flex items-center">
                <REICheckBox
                  id="depe-chk-calculatedepreciation"
                  checked={depreciationEstimation?.CalculateDepreciation == "1" ? true : false}
                  onChange={(e) => setValues("CalculateDepreciation", e.checked ? "1" : "0")}
                  label="Calculate depreciation"
                ></REICheckBox>
              </div>
            </div>
          </div>
          {analysedProperty?.PropertyDetail.CountryId == 1 && (
            <>
              <div className="flex flex-wrap mb-4">
                <div className="w-full md:w-1/2 p-2">
                  <div className="flex items-center justify-evenly">
                    <label className="w-1/2 text-left" htmlFor="Rates">
                      Quality
                    </label>
                    <REIDropDown
                      id="depe-drop-quality"
                      data={PROPERTY_QUALITY}
                      onSelectionChange={(e) => setValues("Quality", Array.isArray(e.value) ? e.value[0] : e.value)}
                      placeholder="Select"
                      value={depreciationEstimation?.Quality}
                      optionValue="value"
                      label="level"
                      className="w-1/2"
                    ></REIDropDown>
                  </div>
                </div>
                <div className="w-full md:w-1/2 p-2">
                  <div className="flex items-center justify-evenly">
                    <label className="w-1/2 text-left" htmlFor="Rates">
                      Property type
                    </label>
                    <REIDropDown
                      id="depe-drop-propertytype"
                      data={PROPERTY_TYPE}
                      onSelectionChange={(e) =>
                        setValues("WBPropertyTypeId", Array.isArray(e.value) ? e.value[0] : e.value)
                      }
                      placeholder="Select"
                      value={parseInt(depreciationEstimation?.WBPropertyTypeId ?? "0") || 0}
                      optionValue="value"
                      label="type"
                      className={`w-1/2${errors.WBPropertyTypeId ? " p-invalid" : ""}`}
                    ></REIDropDown>
                  </div>
                  <div className="justify-self-end">
                    {errors.WBPropertyTypeId && (
                      <small className="p-error" style={{ color: "red" }}>
                        {errors.WBPropertyTypeId}
                      </small>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap mb-4">
                <div className="w-full md:w-1/2 p-2">
                  <div className="flex items-center justify-center">
                    <label className="w-1/2 text-left" htmlFor="Rates">
                      Nearest capital city
                    </label>
                    <REIDropDown
                      id="depe-drop-capitalcity"
                      data={AU_CAPITAL_CITY}
                      onSelectionChange={(e) =>
                        setValues("NearestCapitalCity", Array.isArray(e.value) ? e.value[0] : e.value)
                      }
                      placeholder="Select"
                      value={depreciationEstimation?.NearestCapitalCity}
                      optionValue="value"
                      label="city"
                      className="w-1/2"
                    ></REIDropDown>
                  </div>
                </div>
                <div className="w-full md:w-1/2 p-2">
                  <div className="flex items-center justify-center">
                    <label className="w-1/2 items-center text-left" htmlFor="Rates">
                      Construction year complete
                    </label>
                    <REIDropDown
                      id="depe-drop-constructionyear"
                      data={AU_CONSTRUCTION_YEAR}
                      onSelectionChange={(e) =>
                        setValues("ConstructionYearComplete", Array.isArray(e.value) ? e.value[0] : e.value)
                      }
                      placeholder="Select"
                      value={depreciationEstimation?.ConstructionYearComplete}
                      optionValue="value"
                      label="year"
                      className="w-1/2"
                    ></REIDropDown>
                  </div>
                </div>
              </div>
            </>
          )}
          {analysedProperty?.PropertyDetail.CountryId == 2 && (
            <>
              <div className="flex flex-wrap mb-4">
                <div className="w-full md:w-1/2 p-2">
                  <div className="flex items-center justify-center">
                    <label className="w-1/2 items-center text-left" htmlFor="Rates">
                      Construction year complete
                    </label>
                    <REIDropDown
                      id="depe-drop-constructionyearnz"
                      data={AU_CONSTRUCTION_YEAR}
                      onSelectionChange={(e) =>
                        setValues("ConstructionYearComplete", Array.isArray(e.value) ? e.value[0] : e.value)
                      }
                      placeholder="Select"
                      value={depreciationEstimation?.ConstructionYearComplete}
                      optionValue="value"
                      label="year"
                      className={`w-1/2${errors.ConstructionYearComplete ? " p-invalid" : ""}`}
                    ></REIDropDown>
                  </div>
                  <div className="justify-self-end">
                    {errors.ConstructionYearComplete && (
                      <small className="p-error" style={{ color: "red" }}>
                        {errors.ConstructionYearComplete}
                      </small>
                    )}
                  </div>
                </div>

                <div className="w-full md:w-1/2 p-2">
                  <div className="flex items-center justify-center">
                    <label className="w-1/2 items-center text-left" htmlFor="Rates">
                      Recent renovations
                    </label>
                    <REIDropDown
                      id="depe-drop-recentrenovations"
                      data={NZ_RENOVATIONS}
                      onSelectionChange={(e) =>
                        setValues("NZRecentRenovation", Array.isArray(e.value) ? e.value[0] : e.value)
                      }
                      placeholder="Select"
                      value={depreciationEstimation?.NZRecentRenovation}
                      optionValue="value"
                      label="renovationType"
                      className="w-1/2"
                    ></REIDropDown>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap mb-4">
                <div className="w-full md:w-1/2 p-2">
                  <div className="flex items-center justify-evenly">
                    <label className="w-1/3 text-left" htmlFor="Rates">
                      Your estimate of land value ($)
                    </label>
                    <InputSlider
                      id="depe-sldr-estimatedlandvalue"
                      value={Number(depreciationEstimation?.NZEstimatedLandValue) || 0}
                      onChange={(e) => setValues("NZEstimatedLandValue", Array.isArray(e.value) ? e.value[0] : e.value)}
                      classNames={`w-1/2 mx-2${errors.NZEstimatedLandValue ? " p-invalid" : ""}`}
                      min={0}
                      step={1}
                      max={2000000}
                    />
                    <REIInputNumber
                      id="depe-num-estimatedlandvalue"
                      value={Number(depreciationEstimation?.NZEstimatedLandValue) || 0}
                      onValueChange={(e) => setValues("NZEstimatedLandValue", e.target.value)}
                      useGrouping={true}
                      className={`w-1/4${errors.NZEstimatedLandValue ? " p-invalid" : ""}`}
                    />
                  </div>
                  <div className="justify-self-end">
                    {errors.NZEstimatedLandValue && (
                      <small className="p-error" style={{ color: "red" }}>
                        {errors.NZEstimatedLandValue}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DepreciationEstimation;
